import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { NotificationPreferences } from '@otw/models'
import { AdminNotificationType } from '@otw/models/notifications/enums/enums'

type NotificationsEnum = AdminNotificationType;

export const useNotificationSubscribers = (userId: string) => {
  const queryClient = useQueryClient()

  // Get user's notification subscriptions
  const { data: notificationSubscriptions } = useQuery({
    queryKey: ['notificationSubscriptions', userId],
    queryFn: async (): Promise<NotificationPreferences> => {
      if (!userId) {
        // Return default preferences if no user ID
        const notificationTypes = Object.values(AdminNotificationType)
        const defaultPreferences: NotificationPreferences = {} as any
        notificationTypes.forEach(type => {
          defaultPreferences[type] = { email: false, sms: false, inApp: false }
        })
        return defaultPreferences
      }

      const notificationTypes = Object.values(AdminNotificationType)
      const subscriptions: NotificationPreferences = {} as any

      // Initialize all notification types with default preferences
      notificationTypes.forEach(type => {
        subscriptions[type] = { email: false, sms: false, inApp: false }
      })

      // Get subscriptions for each notification type
      const subscriptionPromises = notificationTypes.map(async (type) => {
        const subscriberDoc = await getDoc(doc(db, 'notification_subscribers', type.toString()))
        
        if (subscriberDoc.exists()) {
          const data = subscriberDoc.data()
          const emailSubscribers = data?.emailSubscribers || []
          const smsSubscribers = data?.smsSubscribers || []
          const inAppSubscribers = data?.inAppSubscribers || []

          const emailSubscriber = emailSubscribers.find((sub: any) => sub.userId === userId)
          const smsSubscriber = smsSubscribers.find((sub: any) => sub.userId === userId)
          const inAppSubscriber = inAppSubscribers.find((sub: any) => sub.userId === userId)
          const sizes =
            inAppSubscriber?.sizes ?? emailSubscriber?.sizes ?? smsSubscriber?.sizes

          subscriptions[type] = {
            email: !!emailSubscriber,
            sms: !!smsSubscriber,
            inApp: !!inAppSubscriber,
            ...(type === AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED &&
              sizes !== undefined && { sizes }),
          }
        }
      })

      await Promise.all(subscriptionPromises)
      return subscriptions
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
  })

  // Get user data to access email and phone
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null
      const userDoc = await getDoc(doc(db, 'users', userId))
      return userDoc.exists() ? userDoc.data() : null
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!userId,
  })

  // Update notification preferences for a specific type
  const updateNotificationPreference = useMutation({
    mutationFn: async ({ 
      notificationType, 
      preferences 
    }: { 
      notificationType: NotificationsEnum
      preferences: { email: boolean; sms: boolean; inApp: boolean }
    }) => {
      if (!userId) throw new Error('User ID is required')
      if (!userData) throw new Error('User data not available')

      const subscriberDoc = doc(db, 'notification_subscribers', notificationType.toString())
      const docSnapshot = await getDoc(subscriberDoc)
      
      const data = docSnapshot.exists() ? docSnapshot.data() : { emailSubscribers: [], smsSubscribers: [], inAppSubscribers: [] }
      const emailSubscribers = data.emailSubscribers || []
      const smsSubscribers = data.smsSubscribers || []
      const inAppSubscribers = data.inAppSubscribers || []

      // Remove existing entries for this user
      const filteredEmailSubscribers = emailSubscribers.filter((sub: any) => sub.userId !== userId)
      const filteredSmsSubscribers = smsSubscribers.filter((sub: any) => sub.userId !== userId)
      const filteredInAppSubscribers = inAppSubscribers.filter((sub: any) => sub.userId !== userId)

      // Add to email subscribers if email preference is enabled
      if (preferences.email && userData.email) {
        filteredEmailSubscribers.push({
          userId,
          email: userData.email,
          phone: userData.phone || ''
        })
      }

      // Add to SMS subscribers if SMS preference is enabled
      if (preferences.sms && userData.phone) {
        filteredSmsSubscribers.push({
          userId,
          email: userData.email || '',
          phone: userData.phone
        })
      }

      // Add to in-app subscribers if in-app preference is enabled
      if (preferences.inApp) {
        filteredInAppSubscribers.push({
          userId,
          email: userData.email || '',
          phone: userData.phone || ''
        })
      }

      // Update the document
      await setDoc(subscriberDoc, {
        emailSubscribers: filteredEmailSubscribers,
        smsSubscribers: filteredSmsSubscribers,
        inAppSubscribers: filteredInAppSubscribers,
        updatedAt: new Date().toISOString()
      })
    },
    onMutate: async ({ notificationType, preferences }) => {
      await queryClient.cancelQueries({ queryKey: ['notificationSubscriptions', userId] })
      
      const previousSubscriptions = queryClient.getQueryData(['notificationSubscriptions', userId])
      
      queryClient.setQueryData(['notificationSubscriptions', userId], (old: NotificationPreferences = {} as any) => ({
        ...old,
        [notificationType]: preferences
      }))

      return { previousSubscriptions }
    },
    onError: (err, variables, context) => {
      if (context?.previousSubscriptions) {
        queryClient.setQueryData(['notificationSubscriptions', userId], context.previousSubscriptions)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationSubscriptions', userId] })
    },
  })

  // Update all notification preferences at once
  const updateNotificationPreferences = useMutation({
    mutationFn: async (preferences: NotificationPreferences) => {
      if (!userId) throw new Error('User ID is required')
      if (!userData) throw new Error('User data not available')

      const notificationTypes = Object.values(AdminNotificationType)
      
      // Update each notification type
      const updatePromises = notificationTypes.map(async (type) => {
        const subscriberDoc = doc(db, 'notification_subscribers', type.toString())
        const docSnapshot = await getDoc(subscriberDoc)
        
        const data = docSnapshot.exists() ? docSnapshot.data() : { emailSubscribers: [], smsSubscribers: [], inAppSubscribers: [] }
        const emailSubscribers = data.emailSubscribers || []
        const smsSubscribers = data.smsSubscribers || []
        const inAppSubscribers = data.inAppSubscribers || []

        // Remove existing entries for this user
        const filteredEmailSubscribers = emailSubscribers.filter((sub: any) => sub.userId !== userId)
        const filteredSmsSubscribers = smsSubscribers.filter((sub: any) => sub.userId !== userId)
        const filteredInAppSubscribers = inAppSubscribers.filter((sub: any) => sub.userId !== userId)

        const typePreferences = preferences[type] || { email: false, sms: false, inApp: false }
        const subscriberPayload = (sizes?: string[]) => {
          const base = { userId, email: userData.email || '', phone: userData.phone || '' }
          // Only add sizes for vehicle added when user has selected specific sizes (empty/undefined = all sizes)
          return type === AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED &&
            sizes !== undefined &&
            sizes.length > 0
            ? { ...base, sizes }
            : base
        }

        // Add to email subscribers if email preference is enabled
        if (typePreferences.email && userData.email) {
          filteredEmailSubscribers.push(subscriberPayload(typePreferences.sizes))
        }

        // Add to SMS subscribers if SMS preference is enabled
        if (typePreferences.sms && userData.phone) {
          filteredSmsSubscribers.push(subscriberPayload(typePreferences.sizes))
        }

        // Add to in-app subscribers if in-app preference is enabled
        if (typePreferences.inApp) {
          filteredInAppSubscribers.push(subscriberPayload(typePreferences.sizes))
        }

        // Update the document
        await setDoc(subscriberDoc, {
          emailSubscribers: filteredEmailSubscribers,
          smsSubscribers: filteredSmsSubscribers,
          inAppSubscribers: filteredInAppSubscribers,
          updatedAt: new Date().toISOString()
        })
      })

      await Promise.all(updatePromises)
    },
    onMutate: async (newPreferences: NotificationPreferences) => {
      await queryClient.cancelQueries({ queryKey: ['notificationSubscriptions', userId] })
      
      const previousSubscriptions = queryClient.getQueryData(['notificationSubscriptions', userId])
      
      queryClient.setQueryData(['notificationSubscriptions', userId], newPreferences)

      return { previousSubscriptions }
    },
    onError: (err, variables, context) => {
      if (context?.previousSubscriptions) {
        queryClient.setQueryData(['notificationSubscriptions', userId], context.previousSubscriptions)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationSubscriptions', userId] })
    },
  })

  return { 
    notificationSubscriptions, 
    updateNotificationPreference, 
    updateNotificationPreferences 
  }
} 